// @ts-strict-ignore
import React, { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { cloneDeep } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  Input,
  RowsSkeletonLoader,
  toast,
} from '@node-space/ui-components'
import { logSentryError } from '@node-space/utils'
import { Approval, DurationInMilliseconds } from 'constants/General'
import { useApprovalsContext } from 'hooks/context/useApprovalsContext'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useManageApproversMutation } from 'hooks/mutations/useManageApproversMutation'
import { useAccountUsersQuery } from 'hooks/queries/useAccountUsersQuery'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'

const ManageApproversModal = () => {
  const { t } = useTranslation()

  const queryClient = useQueryClient()

  const {
    permissions,
    approvalsConfig: { handleManageApproversModal },
  } = useApprovalsContext()
  const {
    profile: { emailAddress },
  } = useProfileContext()

  const [checkBoxValues, setCheckboxValues] = useState([])
  const [changedCheckBoxValues, setChangedCheckBoxValues] = useState([])
  const [searchInput, setSearchInput] = useState('')

  const {
    data: { results: accountUsers } = {},
    isFetching: isFetchingAccountUsers,
    isSuccess,
  } = useAccountUsersQuery({ max: 100, offset: 0 })
  const { mutate: mutateManageApprovers, isPending: isLoadingManageApproversAction } =
    useManageApproversMutation()

  useEffect(() => {
    setCheckboxValues(accountUsers)
  }, [accountUsers])

  const onCheckboxChange = (index: number) => {
    const updatedCheckboxValues = cloneDeep(checkBoxValues)
    updatedCheckboxValues[index].approver = !updatedCheckboxValues?.[index]?.approver

    // new approver value must be different from Old/API value to effect change
    const oldApproverValue = searchInput
      ? checkBoxValues?.[index]?.approver
      : accountUsers?.[index]?.approver
    if (oldApproverValue !== updatedCheckboxValues[index].approver) {
      changedCheckBoxValues[index] = {
        emailAddress: updatedCheckboxValues[index].emailAddress,
        approver: updatedCheckboxValues[index].approver,
      }
      setChangedCheckBoxValues(changedCheckBoxValues)
    } else {
      delete changedCheckBoxValues[index]
      setChangedCheckBoxValues(changedCheckBoxValues)
    }
    setCheckboxValues(updatedCheckboxValues)
  }

  const selectedApproversCount =
    !isFetchingAccountUsers &&
    !isLoadingManageApproversAction &&
    checkBoxValues?.filter(c => c?.approver)?.length

  const cleanChangedCheckBoxValues = changedCheckBoxValues?.filter(Boolean)

  const handleClose = () => handleManageApproversModal({ isVisible: false })

  const onManageApproversSuccess = () => {
    setSearchInput('')
    setChangedCheckBoxValues([])
    toast({
      header: t('approvals.manageApproversSuccessToastMessage'),
      variant: 'success',
      duration: DurationInMilliseconds.TEN_SECONDS,
    })
    queryClient.invalidateQueries({ queryKey: ['accountUsers'] })
    handleClose()
    track.Amp.track(AmplitudeEvent.EOF_MANAGE_APPROVERS_SUCCESS, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.CLICK,
    })
  }

  const onManageApproversError = (error: unknown) => {
    setChangedCheckBoxValues([])
    toast({
      header: t('oopsSomethingWentWrong'),
      variant: 'error',
      duration: DurationInMilliseconds.TEN_SECONDS,
    })
    track.Amp.track(AmplitudeEvent.EOF_MANAGE_APPROVERS_ERROR, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.CLICK,
    })
    logSentryError('Error from ManageApprovals - handleManageApproversAction', error)
  }

  const handleManageApproversAction = async () => {
    mutateManageApprovers(
      { updateUserRoleGroups: cleanChangedCheckBoxValues },
      {
        onSuccess: () => onManageApproversSuccess(),
        onError: (error: unknown) => onManageApproversError(error),
      }
    )
  }

  const displaySearchField = accountUsers?.length >= Approval.MINIMUM_APPROVERS_FOR_MODAL_SEARCH

  const filterApprovers = (query: string) => {
    const lowerCaseSearchInput = query?.toLowerCase()
    setSearchInput(query)
    const filterResult = accountUsers?.filter(
      value =>
        value?.name?.toLowerCase()?.includes(lowerCaseSearchInput) ||
        value?.emailAddress?.toLowerCase()?.includes(lowerCaseSearchInput) ||
        value?.groups?.[0]?.name?.toLowerCase()?.includes(lowerCaseSearchInput)
    )
    setCheckboxValues(query ? filterResult : accountUsers)
  }

  return (
    <Dialog
      onClose={handleClose}
      open
      type="utility"
      title={`${t('approvals.manageApproversModaltitle')} ${
        !!selectedApproversCount ? `(${selectedApproversCount})` : ''
      } `}
      ActionButtons={
        permissions?.canEdit
          ? [
              <Button
                key="save"
                onClick={handleManageApproversAction}
                loading={isLoadingManageApproversAction}
                disabled={isFetchingAccountUsers || !cleanChangedCheckBoxValues?.length}
              >
                {t('save')}
              </Button>,
              <Button
                key="cancel"
                variant="secondary"
                onClick={handleClose}
                disabled={isFetchingAccountUsers || isLoadingManageApproversAction}
              >
                {t('cancel')}
              </Button>,
            ]
          : [
              <Button key="done" onClick={handleClose} disabled={isFetchingAccountUsers}>
                {t('done')}
              </Button>,
            ]
      }
    >
      <>
        {isFetchingAccountUsers && (
          <Box width="full" role="skeletonTab">
            <RowsSkeletonLoader rows={5} />
          </Box>
        )}
        {!isFetchingAccountUsers && isSuccess && (
          <>
            <Box width="full" paddingB={16}>
              {displaySearchField && (
                <Input
                  testId="filter-search-field"
                  onChange={e => filterApprovers(e.target.value)}
                  searchable={true}
                  value={searchInput}
                  placeholderText={t('approvals.searchTeamMembers')}
                />
              )}
            </Box>
            <Box className="max-h-[60vh] overflow-auto">
              {checkBoxValues?.map((item, rowIndex) =>
                permissions?.canEdit ? (
                  <Box paddingB={16} key={rowIndex}>
                    <Checkbox
                      id={`approver-checkbox-${rowIndex}`}
                      name={`approver-checkbox-${rowIndex}`}
                      testId={`approver-checkbox-${rowIndex}`}
                      {...((item?.emailAddress == emailAddress || item?.isWorkspaceOwner) && {
                        disabled: true,
                      })}
                      label={`${`${item?.name || item?.emailAddress} ${
                        !!item?.groups?.[0]?.name ? `(${item?.groups?.[0]?.name})` : ''
                      }`}`}
                      checked={item?.approver || item?.isWorkspaceOwner}
                      onCheckedChange={() => onCheckboxChange(rowIndex)}
                    />
                  </Box>
                ) : (
                  item?.approver && (
                    <Box paddingB={16} key={rowIndex}>
                      <p className="text-body-regular text-foreground-prominent">{`${`${item?.name || item?.emailAddress} ${
                        !!item?.groups?.[0]?.name ? `(${item?.groups?.[0]?.name})` : ''
                      }`}`}</p>
                    </Box>
                  )
                )
              )}
            </Box>
          </>
        )}
      </>
    </Dialog>
  )
}

export default ManageApproversModal
